import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusBadge } from "@/components/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUSES = ["ALL", "UNASSIGNED", "RESERVED", "ACTIVE", "SUSPENDED", "RETIRED"];

export default function Cards() {
  const [data, setData] = useState({ total: 0, cards: [] });
  const [status, setStatus] = useState("ALL");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [count, setCount] = useState("50");
  const [creating, setCreating] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api
      .get("/cards", { params: { status: status === "ALL" ? undefined : status, search: search || undefined, limit: 1000 } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load cards"));
  }, [status, search]);

  useEffect(() => { load(); }, [status]);

  const generate = async () => {
    setCreating(true);
    try {
      const r = await api.post("/cards/bulk", { count: parseInt(count) });
      toast.success(`Created ${r.data.created} cards`);
      setGenOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const toggle = (code) =>
    setSelected((s) => (s.includes(code) ? s.filter((c) => c !== code) : [...s, code]));

  const allSelected = data.cards.length > 0 && selected.length === data.cards.length;
  const toggleAll = () => setSelected(allSelected ? [] : data.cards.map((c) => c.card_code));

  const exportSelected = async () => {
    if (selected.length === 0) return toast.error("Select at least one card");
    try {
      const r = await api.post("/cards/export", { card_codes: selected, format: "png" }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "revnex_qr_export.zip";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div data-testid="cards-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Cards</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportSelected} disabled={selected.length === 0} data-testid="export-selected-button">
            Export QR ({selected.length})
          </Button>
          <Dialog open={genOpen} onOpenChange={setGenOpen}>
            <DialogTrigger asChild>
              <Button data-testid="generate-cards-button">Generate Cards</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Bulk Generate Cards</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <p className="text-sm text-gray-500">
                  Cards are created UNASSIGNED with permanent Card IDs and URLs.
                </p>
                <Select value={count} onValueChange={setCount}>
                  <SelectTrigger data-testid="bulk-count-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["10", "25", "50", "100", "500"].map((c) => (
                      <SelectItem key={c} value={c}>{c} cards</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button onClick={generate} disabled={creating} data-testid="confirm-generate-button">
                  {creating ? "Generating..." : "Generate"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="status-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          placeholder="Search card code or location..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          className="max-w-xs"
          data-testid="card-search-input"
        />
        <Button variant="outline" onClick={load} data-testid="card-search-button">Search</Button>
      </div>

      <div className="bg-white border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-checkbox" />
              </TableHead>
              <TableHead>Card ID</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Business</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>NFC</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.cards.map((c) => (
              <TableRow key={c.card_code} data-testid={`card-row-${c.card_code}`}>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selected.includes(c.card_code)}
                    onCheckedChange={() => toggle(c.card_code)}
                    data-testid={`select-card-${c.card_code}`}
                  />
                </TableCell>
                <TableCell
                  className="font-mono font-medium cursor-pointer"
                  onClick={() => navigate(`/cards/${c.card_code}`)}
                >
                  {c.card_code}
                </TableCell>
                <TableCell><StatusBadge status={c.status} /></TableCell>
                <TableCell>{c.business?.business_name || <span className="text-gray-400">—</span>}</TableCell>
                <TableCell>{c.location || <span className="text-gray-400">—</span>}</TableCell>
                <TableCell>{c.nfc_programmed ? "✓" : <span className="text-gray-400">—</span>}</TableCell>
              </TableRow>
            ))}
            {data.cards.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-8">No cards. Generate some to get started.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <p className="text-sm text-gray-400 mt-3">{data.total} cards total</p>
    </div>
  );
}
